import { useState, useEffect } from "react";
import { DealerLayout } from "@/components/shared/DealerLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { getMyEmployees, createMyEmployee, updateMyEmployee, deleteMyEmployee, getDealerAssignedLocations } from "@/lib/api";
import { EmployeeStoreAccess } from "@/components/shared/EmployeeStoreAccess";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Users, Plus, Pencil, Trash2, Loader2, UserPlus, Mail, Phone, Shield, Lock, Check, Zap, Eye, EyeOff } from "lucide-react";

const REQUIRED_PERMISSIONS = ["knowledge_base", "announcements", "training", "visits"];
const ALL_PERMISSIONS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "dealer_sales", label: "Dealer Sales" },
  { key: "locations", label: "My Locations" },
  { key: "knowledge_base", label: "Knowledge Base" },
  { key: "announcements", label: "Announcements" },
  { key: "submit_location", label: "Submit New Location" },
  { key: "training", label: "Training" },
  { key: "documents", label: "Documents" },
  { key: "marketplace", label: "Marketplace" },
  { key: "visits", label: "My Visits" },
  { key: "chat", label: "Chat" },
];

const DEFAULT_PERMISSIONS = ALL_PERMISSIONS.map((p) => p.key);

const EMPTY_FORM = { first_name: "", last_name: "", email: "", phone: "", password: "", role_title: "Employee", permissions: DEFAULT_PERMISSIONS, vidapay_username: "", vidapay_password: "", allowed_location_ids: null };

// Backend intentionally omits `vidapay_password` when it hasn't been set so we
// only show the "leave blank to keep" hint for employees that already have one.
const emp_has_vidapay_pw = (emp) => Boolean(emp && emp.vidapay_password);

function EmpRevealSecret({ value, testid }) {
  const [shown, setShown] = useState(false);
  if (!value) return <span className="text-slate-400">—</span>;
  return (
    <span className="inline-flex items-center gap-1 font-mono text-xs" data-testid={testid}>
      {shown ? value : "••••••••"}
      <button
        type="button"
        onClick={() => setShown((s) => !s)}
        className="text-slate-400 hover:text-blue-600"
        aria-label={shown ? "Hide" : "Show"}
        data-testid={`${testid}-toggle`}
      >
        {shown ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
      </button>
    </span>
  );
}


export default function DealerEmployees() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchEmployees = async () => {
    try {
      const data = await getMyEmployees();
      setEmployees(data);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("Only dealer owners can manage employees");
      } else {
        console.error("Error fetching employees:", err);
      }
    } finally {
      setLoading(false);
    }
  };

  const [stores, setStores] = useState([]);
  useEffect(() => { fetchEmployees(); getDealerAssignedLocations().then(setStores).catch(() => setStores([])); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (emp) => {
    setEditing(emp);
    setForm({
      first_name: emp.first_name || "",
      last_name: emp.last_name || "",
      email: emp.email || "",
      phone: emp.phone || "",
      password: "",
      role_title: emp.role_title || "Employee",
      permissions: emp.permissions || DEFAULT_PERMISSIONS,
      allowed_location_ids: emp.allowed_location_ids ?? null,
      vidapay_username: emp.vidapay_username || "",
      vidapay_password: "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.email || !form.first_name) {
      return toast.error("First name and email are required");
    }
    if (!editing && !form.password) {
      return toast.error("Password is required for new employees");
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      // Only send vidapay_password when the owner explicitly typed a new one
      // — a blank value must not wipe out the existing stored password.
      if (!payload.vidapay_password) delete payload.vidapay_password;
      if (editing) {
        await updateMyEmployee(editing.id, payload);
        toast.success("Employee updated");
      } else {
        await createMyEmployee(payload);
        toast.success("Employee created");
      }
      setDialogOpen(false);
      fetchEmployees();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save employee");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (empId) => {
    if (!window.confirm("Are you sure you want to delete this employee? This action cannot be undone.")) return;
    try {
      await deleteMyEmployee(empId);
      setEmployees(employees.filter((e) => e.id !== empId));
      toast.success("Employee deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete employee");
    }
  };

  if (loading) {
    return (
      <DealerLayout title="Employees">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-teal" />
        </div>
      </DealerLayout>
    );
  }

  return (
    <DealerLayout title="Employees">
      <div className="max-w-4xl mx-auto" data-testid="dealer-employees-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading font-bold text-2xl text-navy">My Employees</h1>
            <p className="text-slate-500 font-body text-sm mt-1">Add and manage your store employees</p>
          </div>
          <Button onClick={openCreate} className="bg-teal hover:bg-teal/90 text-white" data-testid="add-employee-btn">
            <UserPlus className="w-4 h-4 mr-2" /> Add Employee
          </Button>
        </div>

        {employees.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 font-body text-lg">No employees yet</p>
              <p className="text-slate-400 font-body text-sm mt-1">Add your first employee to give them portal access</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {employees.map((emp, i) => (
              <motion.div key={emp.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                <Card className="border-slate-200" data-testid={`employee-card-${emp.id}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-teal/10 rounded-full flex items-center justify-center">
                        <span className="text-teal font-semibold text-sm">
                          {(emp.first_name?.[0] || "").toUpperCase()}{(emp.last_name?.[0] || "").toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-heading font-medium text-navy text-sm">
                          {emp.first_name} {emp.last_name}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5">
                          <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{emp.email}</span>
                          {emp.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{emp.phone}</span>}
                          <span className="flex items-center gap-1"><Shield className="w-3 h-3" />{emp.role_title}</span>
                          <span className="flex items-center gap-1"><Lock className="w-3 h-3" />{(emp.permissions || []).length} tabs</span>
                          {emp.vidapay_username && (
                            <span className="flex items-center gap-1 text-blue-600" title="VidaPay credentials on file">
                              <Zap className="w-3 h-3" />{emp.vidapay_username}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${emp.active !== false ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                        {emp.active !== false ? "Active" : "Inactive"}
                      </span>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(emp)} data-testid={`edit-emp-${emp.id}`}>
                        <Pencil className="w-4 h-4 text-slate-500" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(emp.id)} data-testid={`delete-emp-${emp.id}`}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="employee-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-navy">{editing ? "Edit Employee" : "Add Employee"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>First Name *</Label>
                  <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="emp-first-name" />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="emp-last-name" />
                </div>
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="emp-email" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="emp-phone" />
              </div>
              <div>
                <Label>{editing ? "New Password (leave blank to keep)" : "Password *"}</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing ? "Leave blank to keep current" : ""} data-testid="emp-password" />
              </div>
              <div>
                <Label>Role Title</Label>
                <Select value={form.role_title} onValueChange={(v) => setForm({ ...form, role_title: v })}>
                  <SelectTrigger data-testid="emp-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Employee">Employee</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                    <SelectItem value="Sales Associate">Sales Associate</SelectItem>
                    <SelectItem value="Store Manager">Store Manager</SelectItem>
                    <SelectItem value="Assistant Manager">Assistant Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* VidaPay Credentials (owner-managed, optional) */}
              <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="text-sm font-semibold text-blue-800">VidaPay Login (optional)</span>
                </div>
                <p className="text-xs text-slate-500 mb-3">
                  Store this employee&apos;s personal VidaPay username &amp; password. Leave blank if not applicable.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">VidaPay Username</Label>
                    <Input
                      value={form.vidapay_username}
                      onChange={(e) => setForm({ ...form, vidapay_username: e.target.value })}
                      placeholder="username"
                      data-testid="emp-vidapay-username"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">
                      VidaPay Password{editing && emp_has_vidapay_pw(editing) ? " (leave blank to keep)" : ""}
                    </Label>
                    <Input
                      type="password"
                      value={form.vidapay_password}
                      onChange={(e) => setForm({ ...form, vidapay_password: e.target.value })}
                      placeholder={editing && emp_has_vidapay_pw(editing) ? "••••••••" : ""}
                      data-testid="emp-vidapay-password"
                    />
                    {editing && emp_has_vidapay_pw(editing) && (
                      <div className="mt-1 text-xs text-slate-500 flex items-center gap-1">
                        Current:&nbsp;
                        <EmpRevealSecret value={editing.vidapay_password} testid={`emp-current-vidapay-pw-${editing.id}`} />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Permissions Section */}
              <div>
                <Label className="text-sm font-semibold text-navy mb-2 block">Portal Access Permissions</Label>
                <p className="text-xs text-slate-400 mb-3">Select which tabs this employee can access. Some tabs are required and cannot be removed.</p>
                <div className="grid grid-cols-2 gap-2">
                  {ALL_PERMISSIONS.map((perm) => {
                    const isRequired = REQUIRED_PERMISSIONS.includes(perm.key);
                    const isChecked = form.permissions.includes(perm.key);
                    return (
                      <label
                        key={perm.key}
                        className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer transition-all ${
                          isRequired
                            ? "bg-teal/5 border-teal/20 cursor-not-allowed"
                            : isChecked
                            ? "bg-emerald-50 border-emerald-200 hover:border-emerald-300"
                            : "bg-white border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid={`perm-${perm.key}`}
                      >
                        <div className={`w-4.5 h-4.5 min-w-[18px] min-h-[18px] rounded flex items-center justify-center border ${
                          isRequired
                            ? "bg-teal border-teal"
                            : isChecked
                            ? "bg-emerald-500 border-emerald-500"
                            : "bg-white border-slate-300"
                        }`}>
                          {(isRequired || isChecked) && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={isChecked}
                          disabled={isRequired}
                          onChange={(e) => {
                            if (isRequired) return;
                            const newPerms = e.target.checked
                              ? [...form.permissions, perm.key]
                              : form.permissions.filter((p) => p !== perm.key);
                            setForm({ ...form, permissions: newPerms });
                          }}
                        />
                        <span className={`text-sm ${isRequired ? "text-teal font-medium" : "text-slate-700"}`}>
                          {perm.label}
                        </span>
                        {isRequired && (
                          <span className="text-[10px] bg-teal/10 text-teal px-1.5 py-0.5 rounded-full ml-auto font-medium">Required</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
              <EmployeeStoreAccess stores={stores} value={form.allowed_location_ids} onChange={(v) => setForm({ ...form, allowed_location_ids: v })} />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-teal hover:bg-teal/90 text-white" data-testid="save-employee-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editing ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DealerLayout>
  );
}
